import { Component, OnInit, Input, ViewChild, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    ToastController,
    IonModal, IonContent, IonSpinner, IonGrid, IonRow, IonCol, IonIcon,
    InfiniteScrollCustomEvent, IonInfiniteScroll,  IonInfiniteScrollContent 
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { MarvButtonComponent, MarvDrawerComponent, MarvCircleComponent, MarvMessageComponent, MarvSelectComponent, MarvSelectItem } from '@marv/components';
import { MarvModalService } from '@marv/services';
import { 
    PhotoGalleryFiltersComponent, PhotoFiltersValues, PhotoGalleryGroupComponent, PhotoGalleryMozaicComponent, 
    PhotoGalleryItem, PhotoGalleryItemOperation, PhotoGalleryModalComponent
} from '@app/components';
import { OperationCard, OperationCustomerPlanningSetting, Photo } from '@app/models';
import { LoggedUserService, OperationService, PhotoService } from '@app/services';

export class PhotoGalleryMission {
    missionId: number;
    startDate: Date;
    endDate: Date;
    operation: PhotoGalleryItemOperation;
}

@Component({
    selector: 'app-photo-gallery-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    imports: [
        IonModal, IonContent, IonSpinner, IonGrid, IonRow, IonCol, IonIcon,
        IonInfiniteScroll,  IonInfiniteScrollContent, ReactiveFormsModule, TranslatePipe,
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvDrawerComponent), forwardRef(() => MarvCircleComponent), forwardRef(() => MarvMessageComponent),
        forwardRef(() => MarvSelectComponent), forwardRef(() => PhotoGalleryFiltersComponent), forwardRef(() => PhotoGalleryGroupComponent), 
        forwardRef(() => PhotoGalleryMozaicComponent), forwardRef(() => PhotoGalleryModalComponent)
    ]
})
export class PhotoGalleryListComponent  implements OnInit {

    @Input({alias : 'mission', required : false}) mission: PhotoGalleryMission;  
    @Input({alias : 'storeCode', required : false}) storeCode: string;  
    @Input({alias : 'defaultView', required : false}) defaultView: 'group'|'mozaic';  
    @Input({alias : 'defaultSize', required : false}) defaultSize: 'small'|'medium'|'big' = 'medium';  
    @Input({alias : 'showViews', required : false}) showViews: boolean = true;  
    @Input({alias : 'showSizes', required : false}) showSizes: boolean = true;  
    @Input({alias : 'showFilters', required : false}) showFilters: boolean = true;  
    @Input({alias : 'showDownload', required : false}) showDownload: boolean = false;  
    @Input({alias : 'savingFilters', required : false}) savingFilters: boolean = true;  
    @Output() onLoadPhotos = new EventEmitter<number>();

    @ViewChild('drawerPhotoFilters') drawerPhotoFilters: MarvDrawerComponent;
    @ViewChild('modalDownloadPhotos') modalDownloadPhotos: IonModal;

    hideFiltersStore: boolean = false;  
    isFetching: boolean = false; 
    isFetchingDownload: boolean = false; 
    photos: Array<Photo> = [];   
    photoFiltersValues: PhotoFiltersValues;
    viewSelected: 'group'|'mozaic';
    sizeSelected: 'small'|'medium'|'big';
    
    operationsAll: Array<OperationCard> = [];
    operationsSettings: Map<number, OperationCustomerPlanningSetting> = new Map<number, OperationCustomerPlanningSetting>; 
    photoSelected: PhotoGalleryItem|null = null;
    
    photosGroupsItems: Array<MarvSelectItem> = [
        new MarvSelectItem(this.translateService.instant('PHOTO_GROUP_NONE'), 'NONE'),
        new MarvSelectItem(this.translateService.instant('PHOTO_GROUP_BY_RETAILER'), 'BY_RETAILER'),
        new MarvSelectItem(this.translateService.instant('PHOTO_GROUP_BY_LOCATION'), 'BY_LOCATION')
    ];    
    downloadPhotosForm: FormGroup = new FormGroup({
        groupBy: new FormControl('NONE', Validators.required),
    });
    
    constructor(
        private toastController: ToastController,
        private translateService: TranslateService,
        private marvModalService: MarvModalService,
        public loggedUserService: LoggedUserService, 
        private photoService: PhotoService 
    ) { }

    ngOnInit() {
        
        if(!this.defaultView){
            this.defaultView = (this.loggedUserService.loggedUser && this.loggedUserService.loggedUser.photoGalleryView === 'GROUPED' ? 'group' : 'mozaic');
        }
        
        this.photoFiltersValues = new PhotoFiltersValues(this.savingFilters, this.savingFilters, this.defaultView, this.defaultSize);
        this.viewSelected = this.photoFiltersValues.viewSelected;
        this.sizeSelected = this.photoFiltersValues.sizeSelected;
        
        if(this.mission){
            let keyOperation = this.mission.operation.source+'|'+this.mission.operation.id;
            this.photoFiltersValues.startDate = this.mission.startDate;
            this.photoFiltersValues.endDate = this.mission.endDate;
            this.photoFiltersValues.operationsIds = [keyOperation];
            this.photoFiltersValues.missionId = this.mission.missionId;
        }
        
        if(this.storeCode){
            this.photoFiltersValues.storesCodes = [this.storeCode];
            this.hideFiltersStore = true;
        }
        
        this.loadData();
    }
    
    async loadData(){
        this.isFetching = true;        
        let operationsDisplayed: Array<OperationCard> = [];          
        this.operationsAll = OperationService.Operations; 
        this.operationsSettings = OperationService.OperationsSettings;
        for(let operation of this.operationsAll){            
            if(this.filterOperation(operation)){
                operationsDisplayed.push(operation);
            }              
        }   
        await this.loadPhotos(operationsDisplayed);
        this.isFetching = false;   
    }
    
    async loadPhotos(operations: Array<OperationCard>){
        let photos: Array<Photo> = [];
        this.photos = [];
        this.photoSelected = null;
        this.photoFiltersValues.cleanKeywords();
        for(let operation of operations){            
            photos = photos.concat(await this.loadPhotosOperation(operation));
        }        
        this.photos = photos;
        if(typeof this.photoFiltersValues.instantFiltersCountSubject.next === 'function'){
            this.photoFiltersValues.instantFiltersCountSubject.next(this.photoFiltersValues.instantFiltersCount);
        }
        this.onLoadPhotos.emit(this.photos.length);
    }
    
    async loadPhotosOperation(operation: OperationCard): Promise<Photo[]> {
        return this.photoService.loadOperationPhotos(operation, this.photoFiltersValues, this.operationsSettings.get(operation.id));      
    }
    
    filterOperation(operation: OperationCard){
        let keyOperation = operation.type+'|'+operation.id;
        return (!this.photoFiltersValues.operationsIds || this.photoFiltersValues.operationsIds.indexOf(keyOperation) !== -1)
            && (!this.photoFiltersValues.typesMissionsCodes || this.photoFiltersValues.typesMissionsCodes.indexOf(operation.typeOfMissionCode) !== -1);
    }
    
    selectView(view: 'group'|'mozaic'){        
        this.photoFiltersValues.viewSelected = view;
        this.photoFiltersValues.saveFilters();
        this.viewSelected = view;     
    }    
        
    selectSize(size: 'small'|'medium'|'big'){        
        this.photoFiltersValues.sizeSelected = size;
        this.photoFiltersValues.saveFilters();
        this.sizeSelected = size;     
    }    
    
    updateFiltersValues(): Promise<boolean>{
        return new Promise((resolve) => {
            let t = this.photoFiltersValues;
            this.photoFiltersValues = new PhotoFiltersValues();                        
            setTimeout(() => {
                this.photoFiltersValues = t;
                resolve(true);
            });  
        });
    }

    showPhotoFilters() {
        this.drawerPhotoFilters.open();
    }
    
    async onDidDismissDrawerFilters($event: any){
        if($event.detail?.data?.resetDrawer){
            await this.updateFiltersValues();           
        }   
    }
    
    async onChangePhotoFilters(photoFiltersValues: PhotoFiltersValues){        
        this.photoFiltersValues = photoFiltersValues;
        this.loadData();
        this.drawerPhotoFilters.close(false);
    }    
    
    selectPhoto(photo: PhotoGalleryItem){
        this.photoSelected = photo;
    }
    
    onDidDismissModalPhoto(){
        this.photoSelected = null;
    }
    
    openDownloadPhotosModal(){
        this.downloadPhotosForm.controls['groupBy'].setValue('NONE', {emitEvent: false});
        this.marvModalService.open(this.modalDownloadPhotos);
    }
    
    closeDownloadPhotosModal(){
        this.modalDownloadPhotos.dismiss();
    }
    
    async canDismissDownloadPhotosModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    onDidDismissDownloadPhotosModal($event: any){
        this.marvModalService.onDidDismiss($event);
    }
    
    async downloadPhotos(){
        this.isFetchingDownload = true;
        
        let groupBy = this.downloadPhotosForm.controls['groupBy'].value;           
        let response = await this.photoService.bulkDownloadPhotos(this.operationsAll, groupBy, this.photoFiltersValues);        
        this.isFetchingDownload = false;
        
        const toast = await this.toastController.create({
            message: this.translateService.instant(response.message),
            duration: 8000,
            position: 'top',
            color: (response.type === 'ERROR' ? 'danger' : (response.type === 'SUCCESS' ? 'success' : 'medium'))
        });
        await toast.present();
        
        if(response.type === 'SUCCESS'){
            this.closeDownloadPhotosModal();
        }
    }
}
